import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { altaManualNuevoJugadorError } from "../lib/altaManualValidation";
import {
  api,
  ApiError,
  PARTICIPANT_STATUSES,
  type PlayerCategory,
  type PlayerProfile,
  type Tournament,
  type TournamentDivision,
} from "../lib/api";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import GuideHelpButton from "../components/GuideHelpButton";

type EnrollMode = "existing" | "new";

interface AdminTournamentPlayerEnrollPageProps {
  setupMode?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  pre_registered: "Pre-registrado",
  active: "Activo",
  confirmed: "Confirmado",
  dropped: "Dado de baja",
};

export default function AdminTournamentPlayerEnrollPage({
  setupMode = false,
}: AdminTournamentPlayerEnrollPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const playersPath = setupMode
    ? `/admin/tournaments/${tournamentId}/setup/players`
    : `/admin/tournaments/${tournamentId}/players`;

  const [mode, setMode] = useState<EnrollMode>("existing");
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [categories, setCategories] = useState<PlayerCategory[]>([]);
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [pendingSearch, setPendingSearch] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [categoryId, setCategoryId] = useState<string>("");
  // División del torneo (Eduardo 2026-10-02): el alta no mandaba la categoría
  // del torneo y, con varias categorías, el jugador quedaba SIN categoría
  // (fuera de grupos y cuadros). Sale de la liga (?division=, la misma del
  // selector de arriba: cambiar una cambia la otra) y es obligatoria cuando
  // el torneo tiene categorías. Solo singles: las parejas y los equipos se
  // inscriben desde Jugadores («Inscribir como pareja»); un jugador suelto en
  // dobles quedaría en el banco sin pareja y entraría así al cuadro.
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [divisionsState, setDivisionsState] = useState<"loading" | "loaded" | "failed">("loading");
  const [status, setStatus] = useState<string>("pre_registered");
  const [notes, setNotes] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("");
  const [city, setCity] = useState("");

  const enrollableDivisions = useMemo(
    () => divisions.filter((division) => division.is_active !== false && division.play_mode !== "doubles"),
    [divisions],
  );
  const urlDivision = searchParams.get("division") ?? "";
  // Una categoría desactivada o de dobles en la liga no se ofrece; con una
  // sola categoría posible se elige sola.
  const divisionId = enrollableDivisions.some((division) => String(division.id) === urlDivision)
    ? urlDivision
    : enrollableDivisions.length === 1
      ? String(enrollableDivisions[0].id)
      : "";
  const selectedDivision = enrollableDivisions.find((division) => String(division.id) === divisionId) ?? null;
  const tournamentHasDivisions = divisions.length > 0;
  // Volver a la lista en la misma categoría (si no, el selector de arriba
  // salta a la primera y el recién inscrito parece no estar).
  const playersListPath = divisionId ? `${playersPath}?division=${divisionId}` : playersPath;

  function handleDivisionChange(value: string) {
    if (!value) return;
    const next = new URLSearchParams(searchParams);
    next.set("division", value);
    setSearchParams(next, { replace: true, state: location.state });
  }

  const selectedPlayer = useMemo(
    () => players.find((p) => p.id === selectedPlayerId) ?? null,
    [players, selectedPlayerId],
  );

  const loadTournament = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      }
    }
  }, [token, tournamentId]);

  const loadDivisions = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.listDivisions(token, tournamentId);
      setDivisions(response.data.divisions);
      setDivisionsState("loaded");
    } catch {
      // Sin la lista no se sabe en qué categoría inscribir: el alta se frena.
      setDivisionsState("failed");
    }
  }, [token, tournamentId]);

  const loadCategories = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.common.playerCategories(token);
      setCategories(response.data.categories);
    } catch {
      // non-critical
    }
  }, [token]);

  const loadPlayers = useCallback(
    async (term: string) => {
      if (!token) return;
      setSearching(true);
      try {
        const response = await api.players.search(token, {
          search: term || undefined,
          limit: 50,
        });
        setPlayers(response.data.players);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "No pudimos cargar los jugadores.");
        }
      } finally {
        setSearching(false);
      }
    },
    [token],
  );

  useEffect(() => {
    void loadTournament();
    void loadDivisions();
    void loadCategories();
    void loadPlayers("");
  }, [loadTournament, loadDivisions, loadCategories, loadPlayers]);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadPlayers(pendingSearch.trim());
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || Number.isNaN(tournamentId)) return;
    setError(null);

    if (mode === "existing" && !selectedPlayerId) {
      setError("Selecciona un jugador de la lista.");
      return;
    }
    if (divisionsState !== "loaded") {
      setError(
        divisionsState === "failed"
          ? "No pudimos cargar las divisiones del torneo. Recarga la página e intenta otra vez."
          : "Espera a que carguen las divisiones del torneo.",
      );
      return;
    }
    if (tournamentHasDivisions && !divisionId) {
      setError(
        enrollableDivisions.length === 0
          ? "Este torneo no tiene divisiones de singles activas. Las parejas se inscriben desde Jugadores."
          : "Elige la división del torneo en la que se inscribe.",
      );
      return;
    }
    if (mode === "new") {
      // Validación explícita: los inputs de "Nuevo jugador" viven fuera del
      // formulario del botón Inscribir, así que el required del navegador no
      // corre aquí (gate #145). Este guard es la única puerta real.
      const altaError = altaManualNuevoJugadorError({ firstName, phone, email });
      if (altaError) {
        setError(altaError);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await api.participants.enroll(token, tournamentId, {
        player_profile_id: mode === "existing" ? selectedPlayerId : null,
        new_player:
          mode === "new"
            ? {
                first_name: firstName.trim(),
                last_name: lastName.trim() || null,
                phone: phone.trim() || null,
                email: email.trim() || null,
                gender: gender || null,
                city: city.trim() || null,
              }
            : null,
        division_id: divisionId ? Number(divisionId) : null,
        category_id: categoryId ? Number(categoryId) : null,
        status: status || null,
        notes: notes.trim() || null,
      });
      if (setupMode) {
        navigate(playersListPath, { replace: true });
      } else {
        navigate(
          `/admin/tournaments/${tournamentId}/players/${response.data.participant.id}`,
          { replace: true },
        );
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo inscribir al jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={5} />}
        <section className="card">
          <p className="form-error">ID de torneo inválido.</p>
        </section>
      </>
    );
  }

  return (
    <>
      {setupMode && <TournamentSetupProgress current={5} />}
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              <span className="hint-label">
                Agregar jugador
                {tournament ? ` · ${tournament.name}` : ""}
                <GuideHelpButton />
              </span>
            </h1>
            <p className="card__subtitle">
              Inscribe un jugador existente o crea uno nuevo. El primer
              registro de historial se crea automáticamente.
            </p>
          </div>
          <div className="form__actions">
            <Link className="btn btn--ghost" to={playersListPath}>
              Volver a jugadores
            </Link>
          </div>
        </div>

        <div className="form__actions" style={{ marginBottom: "1rem" }}>
          <button
            type="button"
            className={mode === "existing" ? "btn" : "btn btn--ghost"}
            onClick={() => setMode("existing")}
            disabled={submitting}
          >
            Jugador existente
          </button>
          <button
            type="button"
            className={mode === "new" ? "btn" : "btn btn--ghost"}
            onClick={() => setMode("new")}
            disabled={submitting}
          >
            Nuevo jugador
          </button>
        </div>

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </section>

      {mode === "existing" && (
        <section className="card">
          <h2 className="card__title">Buscar jugador</h2>
          <form className="form" onSubmit={handleSearch}>
            <label>
              Término de búsqueda
              <input
                type="search"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Nombre, email o teléfono"
              />
            </label>
            <div className="form__actions">
              <button className="btn" type="submit" disabled={searching}>
                {searching ? "Buscando…" : "Buscar"}
              </button>
            </div>
          </form>

          {players.length === 0 ? (
            <p className="muted">
              {searching ? "Buscando…" : "No hay resultados."}
            </p>
          ) : (
            <ul className="list">
              {players.map((p) => {
                const isSelected = p.id === selectedPlayerId;
                return (
                  <li
                    key={p.id}
                    onClick={() => setSelectedPlayerId(p.id)}
                    style={{
                      cursor: "pointer",
                      borderColor: isSelected
                        ? "rgba(183, 255, 0, 0.5)"
                        : undefined,
                      background: isSelected
                        ? "rgba(183, 255, 0, 0.06)"
                        : undefined,
                    }}
                  >
                    <strong>{p.display_name}</strong>
                    {p.primary_category && (
                      <span className="muted">
                        {" "}
                        · {p.primary_category.name}
                      </span>
                    )}
                    {p.city && <span className="muted"> · {p.city}</span>}
                    {p.phone && <span className="muted"> · {p.phone}</span>}
                    {p.email && <span className="muted"> · {p.email}</span>}
                    {isSelected && (
                      <span className="muted"> · seleccionado</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {mode === "new" && (
        <section className="card">
          <h2 className="card__title">Datos del nuevo jugador</h2>
          <form
            className="form"
            onSubmit={(e) => e.preventDefault()}
            aria-label="new-player-fields"
          >
            <label>
              Nombre
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Apellido (opcional)
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Teléfono
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Ciudad (opcional)
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Género (opcional)
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={submitting}
              >
                <option value="">Sin especificar</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
              </select>
            </label>
          </form>
        </section>
      )}

      <section className="card">
        <h2 className="card__title">Datos de la inscripción</h2>
        <form className="form" onSubmit={handleSubmit}>
          {tournamentHasDivisions && enrollableDivisions.length > 0 ? (
            <label>
              División del torneo
              <select
                value={divisionId}
                onChange={(e) => handleDivisionChange(e.target.value)}
                disabled={submitting}
                required
              >
                {divisionId ? null : <option value="">Elige la división…</option>}
                {enrollableDivisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tournamentHasDivisions && enrollableDivisions.length === 0 ? (
            <p className="muted" data-no-toast>
              Este torneo no tiene divisiones de singles activas. Las parejas se inscriben desde Jugadores.
            </p>
          ) : null}
          <label>
            Categoría (opcional)
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={submitting || categories.length === 0}
            >
              <option value="">Sin categoría</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Estatus inicial
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={submitting}
            >
              {PARTICIPANT_STATUSES.filter((s) => s !== "dropped").map(
                (s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s] ?? s}
                  </option>
                ),
              )}
            </select>
          </label>
          <label>
            Notas (opcional)
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />
          </label>
          {mode === "existing" && selectedPlayer && (
            <p className="muted">
              Vas a inscribir a <strong>{selectedPlayer.display_name}</strong>
              {selectedDivision ? (
                <>
                  {" "}en <strong>{selectedDivision.name}</strong>
                </>
              ) : null}
              .
            </p>
          )}
          <div className="form__actions">
            <button className="btn" type="submit" disabled={submitting || divisionsState === "loading"}>
              {submitting ? "Inscribiendo…" : "Inscribir"}
            </button>
            <Link className="btn btn--ghost" to={playersListPath}>
              Cancelar
            </Link>
          </div>
        </form>
      </section>
      {!setupMode && <TournamentBottomNav tournamentId={tournamentId} />}
    </>
  );
}
